import { ArrowUpRight, Globe, ServerCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, SectionHead, SpotlightCard } from "@/components/Shared";

const SERVICES = [
  {
    id: "web-dev",
    num: "01",
    icon: Globe,
    title: "Website Development",
    desc: "Modern, responsive websites designed to present your brand professionally, communicate your value, and turn visitors into enquiries.",
    items: [
      "Business websites",
      "Portfolio websites",
      "Landing pages",
      "Non-profit & community sites",
    ],
    wide: true,
  },
  {
    id: "it-support",
    num: "02",
    icon: ServerCog,
    title: "IT Support & Solutions",
    desc: "Practical help with the everyday technology your business depends on: set up properly, secured, and easy to live with.",
    items: ["Devices, Wi-Fi & networks", "Cloud email & backups", "Security essentials"],
    wide: false,
  },
];

function ServiceCard({ s }) {
  const Icon = s.icon;
  const navigate = useNavigate();
  return (
    <SpotlightCard
      testid={`service-card-${s.id}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border hairline bg-card p-8 transition-colors duration-300 hover:border-white/20 md:p-10 ${
        s.wide ? "lg:col-span-2" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border hairline bg-ink/60">
          <Icon className="h-5 w-5 text-cy" strokeWidth={1.7} />
        </span>
        <span className="font-mono text-sm text-white/20">{s.num}</span>
      </div>

      <h3 className="mt-7 font-grotesk text-xl font-semibold tracking-tight text-txt sm:text-2xl">
        {s.title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-sub">{s.desc}</p>

      <ul
        className={`mt-7 grid gap-x-6 gap-y-2.5 text-sm text-sub ${
          s.wide ? "sm:grid-cols-2" : ""
        }`}
      >
        {s.items.map((item) => (
          <li key={item} className="flex items-center gap-2.5">
            <span className="h-1 w-1 shrink-0 rounded-full bg-cy/80" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => navigate("/contact")}
        data-testid={`service-cta-${s.id}`}
        className="mt-auto inline-flex items-center gap-2 pt-8 font-grotesk text-sm font-medium text-txt transition-colors duration-300 hover:text-cy"
        aria-label={`Enquire about ${s.title}`}
      >
        Enquire about this
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </SpotlightCard>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="scroll-mt-20 py-24 md:py-32">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHead
            eyebrow="What We Do"
            title="Digital solutions for the way you work."
            sub="From your first online presence to everyday technical challenges, we help turn technology into something useful."
          />
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className={s.wide ? "lg:col-span-2" : ""}>
              <ServiceCard s={s} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
